/**
 * Inline editable quantity cells for the inventory detail dialog.
 *
 * Two variants share the same logic, only the wrapping JSX/styling differ:
 *  - <QtyCell />       : desktop table cell (table-aligned, transparent borders, hover ring)
 *  - <MobileQtyCell /> : mobile card label-input pair (compact, with prefix label)
 *
 * The component is purely presentational: state changes go through the
 * `onCommit(inv, field, parsedValue)` callback supplied by the parent (auto-save on blur).
 *
 * Props:
 *  - inv          : Inventory record
 *  - field        : 'quantity_reserve' | 'quantity_stand' | 'quantity_siege'
 *  - value        : current persisted base-unit value of the field
 *  - capacityValue: number — supplier_product.capacity_value (1 if absent)
 *  - mode         : 'purchase' | 'base'
 *  - canEdit      : boolean
 *  - onCommit     : async (inv, field, rawValueString) => void
 *  - align        : tailwind text-align class (desktop only)
 *  - label        : string (mobile only)
 *  - variableWeight : boolean — supplier_product.variable_weight; when false,
 *                     the input restricts step to 0.1 and rounds the typed
 *                     value to one decimal on commit (business rule: non
 *                     variable-weight products should not carry more precision).
 */
import React, { useEffect, useState } from 'react';

function formatDisplayedValue(value, mode, capacityValue) {
    const displayed = mode === 'purchase' && capacityValue > 0 ? value / capacityValue : value;
    if (!(displayed > 0)) return '';
    return Number.isInteger(displayed)
        ? String(displayed)
        : displayed.toFixed(2).replace(/\.?0+$/, '');
}

// Round a raw input value to 1 decimal place. Non-numeric or empty input is
// forwarded unchanged so the caller can still detect a cleared field.
function roundToOneDecimal(raw) {
    if (raw === '' || raw === null || raw === undefined) return raw;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return raw;
    return (Math.round(n * 10) / 10).toString();
}

export function QtyCell({ inv, field, value, capacityValue, mode, canEdit, onCommit, align = 'text-right', variableWeight = true }) {
    const formatted = formatDisplayedValue(value, mode, capacityValue);
    const [localValue, setLocalValue] = useState(formatted);
    useEffect(() => { setLocalValue(formatted); }, [formatted]);
    if (!canEdit) {
        return <span>{formatted || '-'}</span>;
    }
    const step = variableWeight ? 'any' : '0.1';
    const handleBlur = () => {
        const normalized = variableWeight ? localValue : roundToOneDecimal(localValue);
        if (normalized !== localValue) setLocalValue(normalized);
        onCommit(inv, field, normalized);
    };
    return (
        <input
            type="number"
            step={step}
            min="0"
            inputMode="decimal"
            value={localValue}
            placeholder="-"
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={handleBlur}
            onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
                if (e.key === 'Escape') { setLocalValue(formatted); e.target.blur(); }
            }}
            className={`w-16 px-1 py-0.5 text-sm ${align} border border-transparent hover:border-slate-200 focus:border-emerald-400 focus:bg-emerald-50/30 rounded outline-none transition-colors tabular-nums`}
            data-testid={`inline-${field}-${inv.id || `${inv.industrial_product_id}-${inv.supplier_product_id || 'none'}`}`}
        />
    );
}

export function MobileQtyCell({ inv, field, value, capacityValue, mode, canEdit, onCommit, label, variableWeight = true }) {
    const formatted = formatDisplayedValue(value, mode, capacityValue);
    const [localValue, setLocalValue] = useState(formatted);
    useEffect(() => { setLocalValue(formatted); }, [formatted]);
    const step = variableWeight ? 'any' : '0.1';
    const handleBlur = () => {
        const normalized = variableWeight ? localValue : roundToOneDecimal(localValue);
        if (normalized !== localValue) setLocalValue(normalized);
        onCommit(inv, field, normalized);
    };
    return (
        <label className="flex items-center gap-1 text-[10px] text-slate-400">
            <span>{label}</span>
            <input
                type="number"
                step={step}
                min="0"
                inputMode="decimal"
                value={localValue}
                placeholder="-"
                onChange={(e) => setLocalValue(e.target.value)}
                onFocus={(e) => e.target.select()}
                onBlur={handleBlur}
                className="w-10 px-1 py-0.5 text-xs text-center font-medium text-slate-700 border border-slate-200 focus:border-emerald-400 focus:bg-emerald-50/30 rounded outline-none tabular-nums"
                disabled={!canEdit}
                data-testid={`mobile-inline-${field}-${inv.id || `${inv.industrial_product_id}-${inv.supplier_product_id || 'none'}`}`}
            />
        </label>
    );
}
